import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { bestEx } from "@/api/bestEx";
import { TokenStore } from "@/api/http";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * Best-execution reports — RTS 27 (per-venue, quarterly) and RTS 28
 * (top-5 venues per asset class, annual). Both rendered as JSON
 * tables; CSV download for ESMA / ARM submission.
 */
export function AdminBestExecutionPage() {
  const [tab, setTab] = useState<"rts27" | "rts28">("rts27");
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">Best execution</h1>
          <p className="text-2xs text-term-mute">
            MIFID II RTS 27 (per-venue execution quality, quarterly) and
            RTS 28 (top-5 venues per asset class, annual). Built from
            <code className="mx-1">execution_quality_snapshots</code>
            captured at fill time.
          </p>
        </div>
        <div className="flex gap-1">
          <Button variant={tab === "rts27" ? "primary" : "ghost"} onClick={() => setTab("rts27")}>
            RTS 27
          </Button>
          <Button variant={tab === "rts28" ? "primary" : "ghost"} onClick={() => setTab("rts28")}>
            RTS 28
          </Button>
        </div>
      </div>
      {tab === "rts27" ? <Rts27Panel /> : <Rts28Panel />}
    </div>
  );
}

function Rts27Panel() {
  const today = new Date();
  const yyyy = today.getUTCFullYear();
  const q = Math.floor(today.getUTCMonth() / 3) + 1;
  const [period, setPeriod] = useState<string>(`${yyyy}-Q${q}`);
  const list = useQuery({
    queryKey: ["bestex-rts27", period],
    queryFn: () => bestEx.rts27(period),
  });

  return (
    <>
      <Card>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <Label htmlFor="rts27-period">Period</Label>
            <Input
              id="rts27-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="2026-Q1"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={() => list.refetch()}>Refresh</Button>
          </div>
          <div className="flex items-end justify-end">
            <DownloadCsvButton href={bestEx.rts27CsvUrl(period)} filename={`rts27-${period}.csv`} />
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="overflow-x-auto p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No fills in this period"
              description="RTS 27 needs execution_quality_snapshots rows. None exist for this period yet."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Class</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Venue</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Fills</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Notional</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Avg slip (bps)</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Median slip</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">p99 slip</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Latency (ms)</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((r, i) => (
                  <tr key={i} className="border-t border-term-border">
                    <td className="px-3 py-1.5">
                      <Badge>{r.instrument_class}</Badge>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-term-fg">{r.venue}</td>
                    <td className="px-3 py-1.5 text-right tnum">{r.fill_count}</td>
                    <td className="px-3 py-1.5 text-right tnum">
                      {Number(r.notional_total).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum">{Number(r.avg_slippage_bps).toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right tnum">{Number(r.median_slippage_bps).toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right tnum">{Number(r.p99_slippage_bps).toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right tnum">{r.avg_latency_ms.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function Rts28Panel() {
  const today = new Date();
  const [period, setPeriod] = useState<string>(String(today.getUTCFullYear()));
  const list = useQuery({
    queryKey: ["bestex-rts28", period],
    queryFn: () => bestEx.rts28(period),
  });
  return (
    <>
      <Card>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <Label htmlFor="rts28-period">Year</Label>
            <Input
              id="rts28-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="2026"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={() => list.refetch()}>Refresh</Button>
          </div>
          <div className="flex items-end justify-end">
            <DownloadCsvButton href={bestEx.rts28CsvUrl(period)} filename={`rts28-${period}.csv`} />
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="overflow-x-auto p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No fills in this period"
              description="RTS 28 needs annual execution_quality_snapshots history."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Class</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Rank</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Venue</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">% volume</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">% orders</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((r, i) => (
                  <tr key={i} className="border-t border-term-border">
                    <td className="px-3 py-1.5">
                      <Badge>{r.instrument_class}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right tnum">#{r.rank}</td>
                    <td className="px-3 py-1.5 font-mono text-term-fg">{r.venue}</td>
                    <td className="px-3 py-1.5 text-right tnum">{r.pct_of_volume.toFixed(2)}%</td>
                    <td className="px-3 py-1.5 text-right tnum">{r.pct_of_orders.toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function DownloadCsvButton({ href, filename }: { href: string; filename: string }) {
  const toast = useToast();
  // The CSV endpoint requires auth; fetch with the Bearer token then
  // synthesize a download link from the blob.
  async function download() {
    try {
      const token = TokenStore.get();
      const resp = await fetch(href, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        toast.push(`CSV download failed (${resp.status})`, "error");
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
    } catch (e) {
      toast.push(`CSV download failed: ${(e as Error).message}`, "error");
    }
  }
  return (
    <Button variant="secondary" onClick={download}>
      Download CSV
    </Button>
  );
}
